import React from 'react';
import styled from 'styled-components';
import { UserLicenseType, UserRoles } from '@bb/users';

import { AuthContext, AuthContextType } from 'contexts';
import { Icon as NewIcon, IconName } from '@bb/elements-web';

import { featureEnabled, isContextualChatEnabled } from 'utils';
import { Dropdown } from '../Dropdown';
import { Avatar } from '../Avatar';
import { Text } from '../Typography';
import { Menu, MenuItem, MenuDivider } from '../Menu';
import { Icon } from '../Icon';
import theme from '../theme';
import ChatStatusBadge from './ChatStatusBadge';
import { StyledHeaderItem } from './StyledComponents';

const StyledAnchor = styled.div<{
  $isMobile: boolean;
}>`
  ${({ $isMobile }) => {
    if ($isMobile) {
      return `
        && {
          ${Menu} {
            box-shadow: ${theme.getElevation('bottom')};
            background-color: ${theme.gray1};
            border-radius: 0;
            position: relative;
            top: -6px;
            ${MenuItem} {
              padding-left: 25px;
            }
          }
        }
      `;
    }
    return '';
  }}
}
`;

const StyledArrowIcon = styled(Icon)<{ $overlayVisible: boolean }>`
  && {
    margin: 0 5px;
    ${({ $overlayVisible }) =>
      `transform: scaleY(${$overlayVisible ? -1 : 1});`}
    transition: all 1s;
  }
`;

type UserMenuProps = {
  isMobile: boolean;
  $height: number;
  showingsFeatureOn?: boolean;
};

const UserMenu: React.FC<UserMenuProps> = ({
  isMobile,
  $height,
  showingsFeatureOn,
}) => {
  const { auth, logout, hasFeature } =
    React.useContext<AuthContextType>(AuthContext);
  const [overlayVisible, setOverlayVisible] = React.useState(false);
  const anchorRef = React.useRef(null);

  return (
    <>
      <Dropdown
        trigger={['click']}
        open={overlayVisible}
        onOpenChange={visible => setOverlayVisible(visible)}
        getPopupContainer={() => anchorRef.current!}
        dropdownRender={() => (
          <Menu>
            {!auth.isAssumedNotBB && (
              <MenuItem
                key="account"
                to="/profile"
                icon={<Icon type="settings" />}
                onClick={() => setOverlayVisible(false)}
                data-cy="account-settings-button"
              >
                Account Settings
              </MenuItem>
            )}
            {[
              UserRoles.Owner,
              UserRoles.Manager,
              UserRoles['Team Admin'],
            ].includes(auth.role) &&
              hasFeature('TEAMS') &&
              auth.team && (
                <MenuItem
                  key="team"
                  to="/team_settings"
                  icon={<Icon type="people" />}
                  onClick={() => setOverlayVisible(false)}
                  data-cy="team-settings-button"
                >
                  Team Settings
                </MenuItem>
              )}
            {auth.org.type === 'brokerage' && (
              <>
                <MenuItem
                  key="notifications"
                  to="/notifications"
                  icon={<Icon type="bell" />}
                  onClick={() => setOverlayVisible(false)}
                  data-cy="notifications-button"
                >
                  Notification Settings
                </MenuItem>
                {!hasFeature('MY_BROKERAGE') &&
                auth.licenseType !== UserLicenseType.LICENSED ? null : (
                  <MenuItem
                    key="template"
                    to="/template/manage"
                    icon={<Icon type="template" />}
                    onClick={() => setOverlayVisible(false)}
                    data-cy="template-button"
                  >
                    Template Manager
                  </MenuItem>
                )}
                {featureEnabled('supraAutoCoopFeature') &&
                  hasFeature('SUPRA_MVP') && (
                    <MenuItem
                      key="coop"
                      to="/coop/organizations"
                      icon={<NewIcon icon={'connectionTwoWay' as IconName} />}
                      onClick={() => setOverlayVisible(false)}
                      data-cy="coop-button"
                    >
                      Key Co-ops
                    </MenuItem>
                  )}
                {featureEnabled('subscriptionHubFeature') && (
                  <MenuItem
                    key="subscriptions"
                    to="/subscriptions_payments/user"
                    icon={<NewIcon icon={'calendarOutlined' as IconName} />}
                    onClick={() => setOverlayVisible(false)}
                    data-cy="subscriptions-button"
                  >
                    Subscriptions
                  </MenuItem>
                )}
              </>
            )}
            <MenuDivider />
            {auth?.org.id === '57eda146d068d37d07894592' && (
              <MenuItem
                key="lucidpress"
                href="https://www.lucidpress.com/users/login"
                target="_blank"
                rel="noopener noreferrer"
                icon={<Icon type="lucidPress" />}
                onClick={() => setOverlayVisible(false)}
                data-cy="lucidpress-button"
              >
                Lucid Press
              </MenuItem>
            )}
            <MenuItem
              key="logout"
              onClick={logout}
              icon={<Icon type="logout" />}
              data-cy="logout-button"
            >
              Logout
            </MenuItem>
          </Menu>
        )}
      >
        <StyledHeaderItem
          $height={$height}
          $isMobile={isMobile}
          $showBackgroundColor
          className="avatar-button"
          $hasShowingsMadeEasyFeature={showingsFeatureOn}
        >
          <button className="avatar-div" data-cy={'avatar-button'}>
            <Avatar
              className="header-avatar"
              width={showingsFeatureOn && !isMobile ? 28 : 32}
              useAuthUser
            />
            {!showingsFeatureOn && !isMobile && (
              <StyledArrowIcon
                $overlayVisible={overlayVisible}
                type="arrowDownThick"
                width={8}
              />
            )}
          </button>
          {isMobile && (
            <>
              <div>
                <Text type="secondary">Account</Text>
                {!isContextualChatEnabled(auth?.org.mlsTypes) &&
                  hasFeature('CHAT') && <ChatStatusBadge showMargin />}
              </div>
              <div>
                <StyledArrowIcon
                  $overlayVisible={overlayVisible}
                  type="arrowDownThick"
                  width={8}
                />
              </div>
            </>
          )}
        </StyledHeaderItem>
      </Dropdown>
      <StyledAnchor $isMobile={isMobile} ref={anchorRef} />
    </>
  );
};

export default UserMenu;
